(() => {
  const BOOKING_PATH = '/service-booking/api';
  const SESSION_ERROR_CODES = new Set(['invalid_session', 'session_expired', 'session_unavailable']);
  const RETRYABLE_TURNSTILE_SESSION_ERRORS = new Set(['network_error', 'verification_failed']);
  // Matches Workspace's HOLD_LEASE_SECONDS; shown until the hold response
  // brings the real lease.
  const EXPECTED_HOLD_SECONDS = 10 * 60;
  const CHECKING_MESSAGE = 'Checking service availability…';
  const ECRYPT_SRC = 'https://secure.ewaypayments.com/scripts/eCrypt.min.js';
  const CARD_ERROR_CODES = new Set([
    'invalid_card', 'card_declined', 'consent_required',
    'payment_unavailable', 'payment_method_required'
  ]);
  let ecryptPromise = null;
  const loadEcrypt = () => {
    if (window.eCrypt && typeof window.eCrypt.encryptValue === 'function') {
      return Promise.resolve(window.eCrypt);
    }
    if (!ecryptPromise) {
      ecryptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = ECRYPT_SRC;
        script.async = true;
        script.onload = () => (
          window.eCrypt && typeof window.eCrypt.encryptValue === 'function'
            ? resolve(window.eCrypt)
            : reject(new Error('ecrypt_unavailable'))
        );
        script.onerror = () => {
          ecryptPromise = null;
          reject(new Error('ecrypt_unavailable'));
        };
        document.head.appendChild(script);
      });
    }
    return ecryptPromise;
  };
  const luhnValid = (digits) => {
    let sum = 0;
    for (let index = 0; index < digits.length; index += 1) {
      let digit = Number(digits[digits.length - 1 - index]);
      if (index % 2 === 1) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
    }
    return digits.length > 0 && sum % 10 === 0;
  };
  const HOME_FIELDS = [
    'first_name',
    'last_name',
    'email',
    'phone',
    'location',
    'machine_make',
    'machine_model',
    'model',
    'work_requested',
    'problems',
    'hp_field',
    'honeypot',
    'lelit_model',
    'lelitModel',
    'profitec_model',
    'profitecModel',
    'ascaso_model',
    'ascasoModel',
    'expobar_model',
    'expobarModel',
    'ecm_model',
    'ecmModel',
    'rocket_model',
    'rocketModel'
  ];

  class BookingApiError extends Error {
    constructor(message, code = 'booking_unavailable', status = 0) {
      super(message);
      this.name = 'BookingApiError';
      this.code = code;
      this.status = status;
    }
  }

  const normaliseBaseUrl = (value) => String(value || '').trim().replace(/\/+$/, '');
  const pad2 = (value) => (value < 10 ? `0${value}` : String(value));
  const dateOf = (iso) => new Date(`${iso}T00:00:00`);
  const isoOf = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  const addDaysIso = (iso, amount) => {
    const date = dateOf(iso);
    date.setDate(date.getDate() + amount);
    return isoOf(date);
  };
  const mondayOf = (iso) => {
    const date = dateOf(iso);
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    return isoOf(date);
  };
  const newRequestId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    const values = new Uint8Array(16);
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      window.crypto.getRandomValues(values);
    } else {
      for (let index = 0; index < values.length; index += 1) {
        values[index] = Math.floor(Math.random() * 256);
      }
    }
    values[6] = (values[6] & 0x0f) | 0x40;
    values[8] = (values[8] & 0x3f) | 0x80;
    const hex = Array.from(values, (value) => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
  const formatDay = (iso, options = { weekday: 'short', day: 'numeric', month: 'short' }) => (
    dateOf(iso).toLocaleDateString('en-AU', options)
  );
  const weekRangeLabel = (mondayIso) => {
    const monday = dateOf(mondayIso);
    const friday = dateOf(addDaysIso(mondayIso, 4));
    const start = monday.toLocaleDateString(
      'en-AU',
      monday.getMonth() === friday.getMonth() ? { day: 'numeric' } : { day: 'numeric', month: 'short' }
    );
    return `${start} – ${friday.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`;
  };

  const isRetryableTurnstileError = (errorCode) => {
    const code = String(errorCode || '').trim().replace(/[^0-9]/g, '').slice(0, 12);
    return code === '110600'
      || code === '110620'
      || code === '200500'
      || code.startsWith('300')
      || code.startsWith('600');
  };

  let turnstilePromise;
  const loadTurnstile = () => {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstilePromise) return turnstilePromise;

    turnstilePromise = new Promise((resolve, reject) => {
      const callbackName = 'mcsPublicServiceBookingTurnstileLoaded';
      const source = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${callbackName}`;
      let script = document.querySelector('script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]');
      let settled = false;
      const finish = () => {
        if (settled) return;
        if (!window.turnstile) {
          reject(new Error('Turnstile did not load.'));
          return;
        }
        settled = true;
        resolve(window.turnstile);
      };
      const fail = () => {
        if (settled) return;
        settled = true;
        reject(new Error('Turnstile could not be loaded.'));
      };

      window[callbackName] = finish;
      if (!script) {
        script = document.createElement('script');
        script.src = source;
        script.async = true;
        script.defer = true;
        script.addEventListener('load', finish, { once: true });
        script.addEventListener('error', fail, { once: true });
        document.head.appendChild(script);
      } else {
        script.addEventListener('load', finish, { once: true });
        script.addEventListener('error', fail, { once: true });
      }
      window.setTimeout(() => {
        if (window.turnstile) finish();
        else fail();
      }, 15000);
    });
    return turnstilePromise;
  };

  class PublicServiceBooking {
    constructor(root) {
      this.root = root;
      this.form = root.closest('form');
      if (!this.form || this.form.dataset.publicServiceBookingBound === 'true') return;
      this.form.dataset.publicServiceBookingBound = 'true';

      this.baseUrl = normaliseBaseUrl(root.dataset.baseUrl);
      this.bookingType = root.dataset.bookingType || 'home';
      this.siteKey = String(root.dataset.turnstileSiteKey || '').trim();
      this.turnstileAction = root.dataset.turnstileAction || `${this.bookingType}_service_booking`;
      this.successMessage = root.dataset.successMessage || 'Thanks, your service booking has been confirmed.';
      this.configurationErrorMessage = root.dataset.configurationErrorMessage || 'Online booking is not configured. Please call 1300 365 487.';
      this.submitErrorMessage = root.dataset.submitErrorMessage || 'We could not complete your booking. Please try again or call 1300 365 487.';

      this.locationInput = this.form.elements.location;
      this.dateInput = root.querySelector('[data-public-service-date]');
      this.calendar = root.querySelector('[data-public-service-calendar]');
      this.calendarGrid = root.querySelector('[data-public-service-calendar-grid]');
      this.calendarTitle = root.querySelector('[data-public-service-calendar-title]');
      this.calendarPrev = root.querySelector('[data-public-service-calendar-prev]');
      this.calendarNext = root.querySelector('[data-public-service-calendar-next]');
      this.selectedLine = root.querySelector('[data-public-service-selected]');
      this.bookingStatus = root.querySelector('[data-public-service-status]');
      // The mount may sit outside the booking fieldset (the home form keeps it
      // at the foot of the form so verification can run during step 1).
      this.turnstileElement = root.querySelector('[data-public-service-turnstile]')
        || this.form.querySelector('[data-public-service-turnstile]');
      this.formStatus = this.form.querySelector('[data-domestic-service-status]');
      this.submitButton = this.form.querySelector('[data-domestic-service-submit]');
      this.cseKey = String(root.dataset.ewayCseKey || '').trim();
      this.paymentRequired = false;
      this.savedCard = '';
      this.cardSection = this.form.querySelector('[data-public-service-card]');
      this.cardStatus = this.form.querySelector('[data-public-service-card-status]');
      this.cardInputs = {
        number: this.form.querySelector('[data-public-service-card-number]'),
        expiry: this.form.querySelector('[data-public-service-card-expiry]'),
        cvv: this.form.querySelector('[data-public-service-card-cvv]'),
        name: this.form.querySelector('[data-public-service-card-name]')
      };
      this.cardChoices = Array.from(this.form.querySelectorAll('[data-public-service-card-choice]'));
      this.cardNow = this.form.querySelector('[data-public-service-card-now]');
      this.cardDropOffNote = this.form.querySelector('[data-public-service-card-dropoff]');
      this.cardChoices.forEach((input) => {
        input.addEventListener('change', () => this.applyPaymentChoice());
      });
      this.bindCardFormatting();
      this.submitText = this.form.querySelector('[data-domestic-service-submit-text]');

      this.requestId = newRequestId();
      this.sessionToken = '';
      this.sessionCreating = false;
      this.securityState = 'waiting';
      this.securityErrorMessage = '';
      this.turnstileRetryCount = 0;
      this.turnstileRetryTimer = null;
      this.turnstileObserver = null;
      this.turnstileApi = null;
      this.securityEligibilityTimer = null;
      this.widgetId = null;
      this.formStartedAt = new Date().toISOString();
      this.availabilitySequence = 0;
      this.availability = {
        dates: new Set(),
        minimum: '',
        maximum: '',
        weekStart: ''
      };
      this.hold = {
        status: 'none',
        token: '',
        date: '',
        expiresAt: 0,
        timer: null,
        operation: 0,
        pending: null,
        confirming: false,
        confirmed: false
      };

      this.bindEvents();
      this.turnstileElement.hidden = true;
      this.resetCalendar();
      this.initialiseTurnstile();
      this.startSecurityEligibilityWatcher();
    }

    endpoint(path) {
      return `${this.baseUrl}${BOOKING_PATH}${path}`;
    }

    async post(path, payload, options = {}) {
      let response;
      try {
        response = await fetch(this.endpoint(path), {
          method: 'POST',
          credentials: 'same-origin',
          cache: 'no-store',
          keepalive: options.keepalive === true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } catch (error) {
        throw new BookingApiError('Online booking is temporarily unavailable.', 'network_error');
      }

      let result = {};
      try {
        result = await response.json();
      } catch (error) {
        result = {};
      }
      if (!response.ok || result.success === false) {
        throw new BookingApiError(
          result.error || this.submitErrorMessage,
          result.code || 'booking_unavailable',
          response.status
        );
      }
      return result;
    }

    bindEvents() {
      this.calendarGrid?.addEventListener('click', (event) => {
        const slot = event.target.closest('.public-service-booking__slot');
        if (!slot || slot.disabled) return;
        this.selectDate(slot.dataset.date);
      });
      this.calendarPrev?.addEventListener('click', () => this.shiftWeek(-1));
      this.calendarNext?.addEventListener('click', () => this.shiftWeek(1));
      this.locationInput?.addEventListener('change', () => this.locationChanged());
      this.form.addEventListener('input', (event) => this.detailsChanged(event));
      this.form.addEventListener('change', (event) => this.detailsChanged(event));
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.form.addEventListener('reset', () => {
        window.setTimeout(() => this.afterReset(), 0);
      });
      window.addEventListener('pagehide', () => this.releaseOnPageHide());
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted || !this.hold.token || this.hold.confirmed) return;
        this.releaseCurrentHold('page_restored');
        this.fetchAvailability('Your previous reservation was released. Please select the date again.');
      });
    }

    initialiseTurnstile() {
      if (!this.baseUrl || !this.siteKey || !this.turnstileElement) {
        this.securityState = 'failed';
        this.securityErrorMessage = this.configurationErrorMessage;
        this.setBookingStatus(this.configurationErrorMessage, 'error');
        return;
      }
      loadTurnstile().then((turnstile) => {
        this.turnstileApi = turnstile;
        this.maybeStartSecurity();
      }).catch(() => {
        this.securityState = 'failed';
        this.securityErrorMessage = 'Security verification could not load. Please refresh the page or call 1300 365 487.';
        this.setBookingStatus(this.securityErrorMessage, 'error');
      });
    }

    // Availability needs only a session and a service centre, so the security
    // check starts as soon as a location is chosen. The full details are still
    // validated before a date can be held.
    prerequisitesComplete() {
      return Boolean(this.locationValue());
    }

    maybeStartSecurity() {
      if (this.sessionToken || this.sessionCreating || this.widgetId !== null || this.turnstileObserver) return;
      if (!this.prerequisitesComplete()) {
        this.securityState = 'waiting';
        this.turnstileElement.hidden = true;
        if (this.securityErrorMessage) {
          this.setBookingStatus(this.securityErrorMessage, 'error');
        } else {
          this.setBookingStatus('');
        }
        return;
      }
      if (!this.turnstileApi) {
        this.securityState = 'loading';
        this.setBookingStatus(CHECKING_MESSAGE, 'loading');
        return;
      }
      this.renderTurnstileWhenVisible(this.turnstileApi);
    }

    startSecurityEligibilityWatcher() {
      if (this.securityEligibilityTimer) return;
      const check = () => {
        this.maybeStartSecurity();
        if (this.sessionToken || this.widgetId !== null || this.turnstileObserver) {
          this.clearSecurityEligibilityWatcher();
        }
      };
      check();
      if (!this.sessionToken && this.widgetId === null && !this.turnstileObserver) {
        this.securityEligibilityTimer = window.setInterval(check, 300);
      }
    }

    clearSecurityEligibilityWatcher() {
      if (!this.securityEligibilityTimer) return;
      window.clearInterval(this.securityEligibilityTimer);
      this.securityEligibilityTimer = null;
    }

    renderTurnstileWhenVisible(turnstile) {
      if (this.widgetId !== null || this.turnstileObserver) return;
      // A hidden element cannot intersect, so reveal the empty mount before
      // waiting for it to approach the viewport.
      this.turnstileElement.hidden = false;
      const render = () => {
        this.turnstileObserver?.disconnect();
        this.turnstileObserver = null;
        window.requestAnimationFrame(() => this.renderTurnstile(turnstile));
      };

      if (!('IntersectionObserver' in window)) {
        render();
        return;
      }

      this.turnstileObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) render();
      }, { rootMargin: '400px 0px' });
      this.turnstileObserver.observe(this.turnstileElement);
      this.clearSecurityEligibilityWatcher();
    }

    renderTurnstile(turnstile) {
      if (this.widgetId !== null || this.sessionToken) return;
      this.turnstileElement.hidden = false;
      this.securityState = 'loading';
      this.setBookingStatus(CHECKING_MESSAGE, 'loading');

      try {
        this.widgetId = turnstile.render(this.turnstileElement, {
          sitekey: this.siteKey,
          action: this.turnstileAction,
          theme: 'light',
          size: 'normal',
          execution: 'render',
          appearance: 'interaction-only',
          retry: 'never',
          'refresh-expired': 'never',
          'refresh-timeout': 'never',
          callback: (token) => {
            this.clearTurnstileRetryTimer();
            delete this.root.dataset.turnstileErrorCode;
            this.securityState = 'verifying';
            this.securityErrorMessage = '';
            this.createSession(token);
          },
          'before-interactive-callback': () => {
            this.turnstileElement.classList.add('is-interactive');
            if (!this.sessionToken) {
              this.setBookingStatus('Complete the security check to load booking dates.');
            }
          },
          'error-callback': (errorCode) => {
            if (!this.sessionToken) {
              const reference = String(errorCode || '').trim().replace(/[^0-9]/g, '').slice(0, 12);
              this.root.dataset.turnstileErrorCode = reference || 'unknown';
              if (reference) {
                console.warn(`[PublicServiceBooking] Turnstile client error ${reference}.`);
              }
              if (isRetryableTurnstileError(reference) && this.scheduleTurnstileRetry(turnstile)) {
                return true;
              }
              this.securityState = 'failed';
              this.securityErrorMessage = reference
                ? `Security verification failed. Refresh the page and try again. Reference: ${reference}.`
                : 'Security verification failed. Refresh the page and try again.';
              this.setBookingStatus(this.securityErrorMessage, 'error');
            }
            return false;
          },
          'expired-callback': () => {
            if (!this.sessionToken && !this.scheduleTurnstileRetry(turnstile)) {
              this.securityState = 'failed';
              this.securityErrorMessage = 'Security verification expired. Refresh the page and try again.';
              this.setBookingStatus(this.securityErrorMessage, 'error');
            }
          },
          'timeout-callback': () => {
            if (!this.sessionToken && !this.scheduleTurnstileRetry(turnstile)) {
              this.securityState = 'failed';
              this.securityErrorMessage = 'Security verification timed out. Refresh the page and try again.';
              this.setBookingStatus(this.securityErrorMessage, 'error');
            }
          },
          'unsupported-callback': () => {
            if (!this.sessionToken) {
              this.securityState = 'failed';
              this.securityErrorMessage = 'This browser cannot complete the security check. Try another browser or call 1300 365 487.';
              this.setBookingStatus(this.securityErrorMessage, 'error');
            }
          }
        });
      } catch (error) {
        this.securityState = 'failed';
        this.securityErrorMessage = 'Security verification could not load. Please refresh the page or call 1300 365 487.';
        this.setBookingStatus(this.securityErrorMessage, 'error');
      }
    }

    clearTurnstileRetryTimer() {
      if (!this.turnstileRetryTimer) return;
      window.clearTimeout(this.turnstileRetryTimer);
      this.turnstileRetryTimer = null;
    }

    scheduleTurnstileRetry(turnstile) {
      if (
        this.sessionToken
        || this.widgetId === null
        || this.turnstileRetryCount >= 1
        || this.turnstileRetryTimer
      ) return false;

      this.turnstileRetryCount += 1;
      this.securityState = 'loading';
      this.securityErrorMessage = '';
      this.setBookingStatus(CHECKING_MESSAGE, 'loading');
      this.turnstileRetryTimer = window.setTimeout(() => {
        this.turnstileRetryTimer = null;
        if (this.sessionToken || this.widgetId === null) return;
        try {
          turnstile.remove(this.widgetId);
        } catch (error) {
          // Rendering a fresh widget below is the recovery path.
        }
        this.widgetId = null;
        this.turnstileElement.replaceChildren();
        this.turnstileElement.classList.remove('is-interactive');
        this.renderTurnstile(turnstile);
      }, 1000);
      return true;
    }

    resetTurnstileRetryBudget() {
      this.clearTurnstileRetryTimer();
      this.turnstileRetryCount = 0;
    }

    async createSession(turnstileToken) {
      if (this.sessionToken || this.sessionCreating || !turnstileToken) return;
      this.sessionCreating = true;
      const payload = {
        booking_type: this.bookingType,
        request_id: this.requestId,
        turnstile_token: turnstileToken
      };
      try {
        let result;
        try {
          result = await this.post('/session', payload);
        } catch (error) {
          if (error.code !== 'network_error') throw error;
          result = await this.post('/session', payload);
        }
        this.sessionToken = result.session_token;
        this.paymentRequired = result.payment_method_required === true;
        this.clearSecurityEligibilityWatcher();
        this.resetTurnstileRetryBudget();
        this.securityState = 'ready';
        this.securityErrorMessage = '';
        this.turnstileElement.hidden = true;
        if (this.locationValue()) {
          await this.fetchAvailability();
        } else {
          this.setBookingStatus('Select a location to view available booking dates.');
        }
      } catch (error) {
        if (
          RETRYABLE_TURNSTILE_SESSION_ERRORS.has(error.code)
          && window.turnstile
          && this.scheduleTurnstileRetry(window.turnstile)
        ) return;
        this.securityState = 'failed';
        this.securityErrorMessage = error.message || this.submitErrorMessage;
        this.setBookingStatus(error.message || this.submitErrorMessage, 'error');
      } finally {
        this.sessionCreating = false;
      }
    }

    locationValue() {
      return String(this.locationInput?.value || '').trim();
    }

    removeTurnstileWidget() {
      this.turnstileObserver?.disconnect();
      this.turnstileObserver = null;
      this.clearTurnstileRetryTimer();
      if (this.turnstileApi && this.widgetId !== null) {
        try {
          this.turnstileApi.remove(this.widgetId);
        } catch (error) {
          // Clearing the mount below is the local cleanup backstop.
        }
      }
      this.widgetId = null;
      this.turnstileElement.replaceChildren();
      this.turnstileElement.classList.remove('is-interactive');
      this.turnstileElement.hidden = true;
    }

    setBookingStatus(message, tone = 'info') {
      if (!this.bookingStatus) return;
      this.bookingStatus.textContent = message || '';
      this.bookingStatus.hidden = !message;
      this.bookingStatus.classList.toggle('public-service-booking__status--error', tone === 'error');
      this.bookingStatus.classList.toggle('public-service-booking__status--loading', tone === 'loading' && Boolean(message));
      this.bookingStatus.setAttribute('role', tone === 'error' ? 'alert' : 'status');
    }

    setFormStatus(message, tone) {
      if (!this.formStatus) return;
      this.formStatus.textContent = message || '';
      this.formStatus.hidden = !message;
      this.formStatus.classList.remove('domestic-service-form__status--success', 'domestic-service-form__status--error');
      if (message) this.formStatus.classList.add(`domestic-service-form__status--${tone}`);
      this.formStatus.setAttribute('role', tone === 'error' ? 'alert' : 'status');
      if (message) this.formStatus.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setSubmitting(submitting) {
      if (this.submitButton) {
        this.submitButton.disabled = submitting;
        this.submitButton.toggleAttribute('aria-busy', submitting);
      }
      if (this.submitText) {
        const idle = this.root.dataset.submitLabel || 'Book Service';
        const busy = this.root.dataset.submittingLabel || 'Booking...';
        this.submitText.textContent = submitting ? busy : idle;
      }
    }

    setCalendarBusy(busy) {
      if (this.calendar) this.calendar.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    resetCalendar(message, tone = 'info') {
      this.availabilitySequence += 1;
      this.availability.dates = new Set();
      this.availability.minimum = '';
      this.availability.maximum = '';
      this.availability.weekStart = '';
      if (this.calendarGrid) this.calendarGrid.textContent = '';
      if (this.calendarTitle) this.calendarTitle.textContent = '';
      if (this.calendarPrev) this.calendarPrev.disabled = true;
      if (this.calendarNext) this.calendarNext.disabled = true;
      if (this.calendar) {
        this.calendar.hidden = true;
        this.calendar.setAttribute('aria-busy', 'false');
      }
      this.clearSelectedDate();
      this.setBookingStatus(message || '', tone);
    }

    async locationChanged() {
      await this.releaseCurrentHold('location_changed');
      const location = this.locationValue();
      if (!location) {
        this.resetCalendar('Select a location to view available booking dates.');
        return;
      }
      if (!this.sessionToken) {
        this.maybeStartSecurity();
        const checking = !(this.securityState === 'failed' && this.securityErrorMessage)
          && this.prerequisitesComplete();
        this.resetCalendar(
          checking ? CHECKING_MESSAGE : (this.securityState === 'failed' ? this.securityErrorMessage : ''),
          checking ? 'loading' : 'info'
        );
        if (this.securityState === 'failed') {
          this.setBookingStatus(this.securityErrorMessage, 'error');
        }
        return;
      }
      await this.fetchAvailability();
    }

    async fetchAvailability(messageAfter = '') {
      const location = this.locationValue();
      if (!this.sessionToken || !location) return;
      const sequence = ++this.availabilitySequence;
      this.setCalendarBusy(true);
      this.setBookingStatus(CHECKING_MESSAGE, 'loading');
      try {
        const result = await this.post('/availability', {
          session_token: this.sessionToken,
          service_center: location,
          days: 60
        });
        if (sequence !== this.availabilitySequence) return;
        const minimum = String(result.window_start || '');
        const serverMaximum = String(result.window_end || '');
        const requestMaximum = minimum ? addDaysIso(minimum, 59) : '';
        this.availability.dates = new Set(
          Array.isArray(result.available_dates)
            ? result.available_dates.filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(String(date)))
            : []
        );
        this.availability.minimum = minimum;
        this.availability.maximum = serverMaximum && requestMaximum
          ? (serverMaximum < requestMaximum ? serverMaximum : requestMaximum)
          : (serverMaximum || requestMaximum);
        // Open on the week of the first free date, so a full or finished
        // current week (e.g. a Friday evening) doesn't open on a row of
        // "Full". The customer can still page back to it.
        const firstOpen = Array.from(this.availability.dates).sort()[0] || minimum;
        this.availability.weekStart = firstOpen ? mondayOf(firstOpen) : '';
        if (!minimum || !this.availability.maximum) {
          throw new BookingApiError(this.submitErrorMessage, 'booking_unavailable');
        }
        if (this.calendar) this.calendar.hidden = false;
        this.renderWeek();
        if (messageAfter) {
          this.setBookingStatus(messageAfter, 'error');
        } else if (!this.availability.dates.size) {
          this.setBookingStatus('No online booking dates are currently available. Please call 1300 365 487.', 'error');
        } else {
          this.setBookingStatus('');
        }
      } catch (error) {
        if (sequence !== this.availabilitySequence) return;
        if (this.handleSessionError(error)) return;
        this.resetCalendar(error.message || this.submitErrorMessage);
        this.setBookingStatus(error.message || this.submitErrorMessage, 'error');
      } finally {
        if (sequence === this.availabilitySequence) this.setCalendarBusy(false);
      }
    }

    renderWeek() {
      const { weekStart, minimum, maximum, dates } = this.availability;
      if (!this.calendarGrid || !weekStart) return;
      this.calendarGrid.textContent = '';
      if (this.calendarTitle) this.calendarTitle.textContent = weekRangeLabel(weekStart);
      if (this.calendarPrev) this.calendarPrev.disabled = weekStart <= mondayOf(minimum);
      if (this.calendarNext) this.calendarNext.disabled = weekStart >= mondayOf(maximum);

      for (let index = 0; index < 5; index += 1) {
        const iso = addDaysIso(weekStart, index);
        const available = iso >= minimum && iso <= maximum && dates.has(iso);
        const selected = this.dateInput?.value === iso;
        const slot = document.createElement('button');
        slot.type = 'button';
        slot.className = 'public-service-booking__slot';
        slot.dataset.date = iso;
        slot.disabled = !available;
        slot.classList.toggle('is-selected', selected);
        slot.setAttribute('aria-pressed', selected ? 'true' : 'false');
        slot.setAttribute(
          'aria-label',
          `${formatDay(iso, { weekday: 'long', day: 'numeric', month: 'long' })}, ${available ? 'available' : 'unavailable'}`
        );
        const date = dateOf(iso);
        // Short and full names are both rendered; CSS shows the full ones on
        // desktop and the short ones on phones.
        const labelled = (className, short, full) => {
          const span = document.createElement('span');
          span.className = className;
          const shortSpan = document.createElement('span');
          shortSpan.className = 'public-service-booking__short';
          shortSpan.textContent = short;
          const fullSpan = document.createElement('span');
          fullSpan.className = 'public-service-booking__full';
          fullSpan.textContent = full;
          span.append(shortSpan, fullSpan);
          return span;
        };
        const day = labelled(
          'public-service-booking__slot-day',
          date.toLocaleDateString('en-AU', { weekday: 'short' }),
          date.toLocaleDateString('en-AU', { weekday: 'long' })
        );
        const detail = available
          ? labelled(
            'public-service-booking__slot-date',
            date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
            date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long' })
          )
          : labelled('public-service-booking__slot-date', 'Full', 'Full');
        slot.append(day, detail);
        this.calendarGrid.appendChild(slot);
      }
      this.updateSelectedLine();
    }

    shiftWeek(amount) {
      const next = addDaysIso(this.availability.weekStart, amount * 7);
      if (next < mondayOf(this.availability.minimum) || next > mondayOf(this.availability.maximum)) return;
      this.availability.weekStart = next;
      this.renderWeek();
    }

    buildBooking() {
      const formData = new FormData(this.form);
      const booking = {};
      HOME_FIELDS.forEach((field) => {
        const value = formData.get(field);
        if (typeof value === 'string') booking[field] = value.trim();
      });
      if (!booking.machine_model) {
        booking.machine_model = booking.lelit_model
          || booking.profitec_model
          || booking.ascaso_model
          || booking.expobar_model
          || booking.ecm_model
          || booking.rocket_model
          || '';
      }
      // The production booking API currently recognises only ECM's former
      // Elektronika value. Keep the selected model in ecm_model and the
      // workshop notes while using that recognised value for validation.
      if (booking.machine_make === 'ECM'
        && booking.ecm_model
        && booking.ecm_model !== 'Elektronika Profi Rotary') {
        const modelNote = `ECM model selected: ${booking.ecm_model}`;
        booking.machine_model = 'Elektronika Profi Rotary';
        booking.problems = booking.problems
          ? `${modelNote}\n${booking.problems}`
          : modelNote;
      }
      booking.page_url = window.location.href;
      booking.submitted_at = new Date().toISOString();
      return booking;
    }

    validateForm() {
      if (!this.locationValue()) {
        const locationControl = this.form.querySelector('[data-domestic-service-location-select]');
        const locationTrigger = this.form.querySelector('[data-domestic-service-location-trigger]');
        locationControl?.classList.add('is-invalid');
        locationTrigger?.setAttribute('aria-invalid', 'true');
        locationTrigger?.focus();
        this.setBookingStatus('Select a location before continuing.', 'error');
        return false;
      }
      return this.form.reportValidity();
    }

    async selectDate(iso) {
      if (!this.sessionToken || !this.availability.dates.has(iso)) return;
      if ((this.hold.status === 'held' || this.hold.status === 'acquiring') && this.hold.date === iso) return;
      if (!this.validateForm()) {
        this.setBookingStatus('Complete the required details above before selecting a booking date.', 'error');
        return;
      }

      const operation = ++this.hold.operation;
      const previousToken = this.hold.token;
      this.stopHoldTimer();
      this.hold.status = 'acquiring';
      this.hold.token = '';
      this.hold.date = iso;
      // Show the date as reserved straight away; the hold response corrects
      // the expiry, and a failed hold clears it and shows the error.
      this.hold.expiresAt = Date.now() + (EXPECTED_HOLD_SECONDS * 1000);
      if (this.dateInput) this.dateInput.value = iso;
      this.setBookingStatus('');
      this.renderWeek();

      const pending = (async () => {
        if (previousToken) await this.releaseToken(previousToken, 'date_changed');
        if (operation !== this.hold.operation) return;
        const result = await this.post('/hold', {
          session_token: this.sessionToken,
          scheduled_date: iso,
          booking: this.buildBooking(),
          form_started_at: this.formStartedAt
        });
        if (operation !== this.hold.operation) {
          await this.releaseToken(result.hold_token, 'superseded');
          return;
        }
        this.hold.status = 'held';
        this.hold.token = result.hold_token;
        this.hold.date = iso;
        const lease = Number(result.lease_seconds || 0);
        this.hold.expiresAt = lease > 0
          ? Date.now() + (lease * 1000)
          : (Date.parse(result.expires_at || '') || 0);
        this.setBookingStatus('');
        this.updateSelectedLine();
        this.startHoldTimer();
      })();
      this.hold.pending = pending;
      try {
        await pending;
      } catch (error) {
        if (operation !== this.hold.operation) return;
        this.clearHoldState();
        if (this.handleSessionError(error)) return;
        if (error.code === 'date_unavailable' || error.code === 'booking_conflict') {
          this.availability.dates.delete(iso);
          this.renderWeek();
          await this.fetchAvailability('That date is no longer available. Please choose another date.');
          return;
        }
        this.setBookingStatus(error.message || this.submitErrorMessage, 'error');
      } finally {
        if (operation === this.hold.operation) this.hold.pending = null;
      }
    }

    // Lets the page (the step panel) follow the held date.
    announceHold() {
      this.root.dispatchEvent(new CustomEvent('public-service-booking:hold-change', {
        bubbles: true,
        detail: { status: this.hold.status, date: this.dateInput?.value || '' }
      }));
    }

    updateSelectedLine() {
      this.updateCardVisibility();
      this.announceHold();
      if (!this.selectedLine) return;
      const iso = this.dateInput?.value || '';
      if (!iso) {
        this.selectedLine.textContent = '';
        this.selectedLine.hidden = true;
        return;
      }
      const day = formatDay(iso, { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '');
      if ((this.hold.status === 'held' || this.hold.status === 'acquiring') && this.hold.expiresAt) {
        this.selectedLine.textContent = `${day} reserved for `;
        const countdown = document.createElement('span');
        countdown.className = 'public-service-booking__hold';
        countdown.dataset.publicServiceHoldCountdown = '';
        this.selectedLine.appendChild(countdown);
        this.renderHoldCountdown();
      } else {
        this.selectedLine.textContent = `Selected: ${day}`;
      }
      this.selectedLine.hidden = false;
    }

    renderHoldCountdown() {
      const countdown = this.selectedLine?.querySelector('[data-public-service-hold-countdown]');
      const mirrors = this.form.querySelectorAll('[data-public-service-hold-mirror]');
      if (!countdown && !mirrors.length) return;
      const total = Math.max(0, Math.ceil((this.hold.expiresAt - Date.now()) / 1000));
      const minutes = Math.floor(total / 60);
      const seconds = total % 60;
      const text = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
      if (countdown) countdown.textContent = text;
      mirrors.forEach((mirror) => { mirror.textContent = text; });
    }

    startHoldTimer() {
      this.stopHoldTimer();
      if (!this.hold.expiresAt) return;
      this.renderHoldCountdown();
      this.hold.timer = window.setInterval(() => {
        if (this.hold.status !== 'held') {
          this.stopHoldTimer();
          return;
        }
        this.renderHoldCountdown();
        if (this.hold.expiresAt <= Date.now()) this.holdExpired();
      }, 1000);
    }

    stopHoldTimer() {
      if (!this.hold.timer) return;
      window.clearInterval(this.hold.timer);
      this.hold.timer = null;
    }

    async holdExpired() {
      const expiredDate = this.hold.date;
      this.clearHoldState();
      if (expiredDate) {
        await this.fetchAvailability('Your reserved date expired. Please select a booking date again.');
      }
    }

    clearSelectedDate() {
      if (this.dateInput) this.dateInput.value = '';
      if (this.selectedLine) {
        this.selectedLine.textContent = '';
        this.selectedLine.hidden = true;
      }
      this.updateCardVisibility();
      this.announceHold();
    }

    // Card details appear under the drop-off date once one is held, and only
    // when Workspace asks for a card (session payment_method_required).
    updateCardVisibility() {
      if (!this.cardSection) return;
      const show = this.paymentRequired
        && Boolean(this.dateInput?.value)
        && (this.hold.status === 'held' || this.hold.status === 'acquiring');
      if (this.cardSection.hidden === !show) return;
      this.cardSection.hidden = !show;
      this.cardChoices.forEach((input) => { input.disabled = !show; });
      this.applyPaymentChoice();
      if (!show) this.setCardStatus('');
    }

    paymentChoice() {
      return this.cardChoices.find((input) => input.checked)?.value || '';
    }

    // Card fields only for "provide now"; "at drop-off" books without a card.
    applyPaymentChoice() {
      const shown = this.cardSection && !this.cardSection.hidden;
      const choice = shown ? this.paymentChoice() : '';
      if (this.cardNow) this.cardNow.hidden = choice !== 'now';
      if (this.cardDropOffNote) this.cardDropOffNote.hidden = choice !== 'drop_off';
      Object.values(this.cardInputs).forEach((input) => {
        if (input) input.disabled = choice !== 'now';
      });
      this.setCardStatus('');
      if (choice === 'now') {
        this.fitExpiryPlaceholder?.();
        loadEcrypt().catch(() => {});
      }
    }

    setCardStatus(message) {
      if (!this.cardStatus) return;
      this.cardStatus.textContent = message || '';
      this.cardStatus.hidden = !message;
    }

    // Card number spaced as it is typed (4-6-5 for American Express), expiry
    // as "MM / YY", security code digits only.
    bindCardFormatting() {
      const { number, expiry, cvv } = this.cardInputs;
      // The expiry placeholder shortens to fit its box (two columns on a
      // laptop, half width on a phone).
      if (expiry) {
        this.fitExpiryPlaceholder = () => {
          const width = expiry.clientWidth;
          if (!width) return;
          if (width >= 230) expiry.placeholder = 'Expiration date (MM / YY)';
          else if (width >= 170) expiry.placeholder = 'Expiry (MM / YY)';
          else expiry.placeholder = 'MM / YY';
        };
        if ('ResizeObserver' in window) new ResizeObserver(this.fitExpiryPlaceholder).observe(expiry);
        window.addEventListener('resize', this.fitExpiryPlaceholder);
      }
      number?.addEventListener('input', () => {
        const digits = number.value.replace(/\D/g, '').slice(0, 19);
        const groups = /^3[47]/.test(digits) ? [4, 6, 5] : [4, 4, 4, 4, 3];
        const parts = [];
        let index = 0;
        groups.forEach((size) => {
          if (index < digits.length) parts.push(digits.slice(index, index + size));
          index += size;
        });
        number.value = parts.join(' ');
      });
      expiry?.addEventListener('input', (event) => {
        let digits = expiry.value.replace(/\D/g, '').slice(0, 4);
        if (digits.length === 1 && Number(digits) > 1) digits = `0${digits}`;
        const deleting = String(event.inputType || '').startsWith('delete');
        if (digits.length > 2) {
          expiry.value = `${digits.slice(0, 2)} / ${digits.slice(2)}`;
        } else {
          expiry.value = digits.length === 2 && !deleting ? `${digits} / ` : digits;
        }
      });
      cvv?.addEventListener('input', () => {
        cvv.value = cvv.value.replace(/\D/g, '').slice(0, 4);
      });
    }

    cardDetails() {
      const text = (input) => String(input?.value || '').trim();
      const digits = (input) => text(input).replace(/\D/g, '');
      const expiry = digits(this.cardInputs.expiry);
      return {
        name: text(this.cardInputs.name),
        number: digits(this.cardInputs.number),
        month: expiry.slice(0, 2),
        year: expiry.slice(2),
        cvv: digits(this.cardInputs.cvv)
      };
    }

    cardProblem(details) {
      const inputs = this.cardInputs;
      if (details.number.length < 12 || details.number.length > 19 || !luhnValid(details.number)) {
        return [inputs.number, 'Check your card number.'];
      }
      const month = Number(details.month);
      if (details.month.length !== 2 || details.year.length !== 2 || month < 1 || month > 12) {
        return [inputs.expiry, 'Enter the expiry date as MM / YY.'];
      }
      const year = 2000 + Number(details.year);
      const now = new Date();
      if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)) {
        return [inputs.expiry, 'This card has expired.'];
      }
      if (details.cvv.length < 3 || details.cvv.length > 4) {
        return [inputs.cvv, 'Enter the 3 or 4 digit security code.'];
      }
      if (!details.name) return [inputs.name, 'Enter the name on your card.'];
      return null;
    }

    // Saves the card against the held date before confirming. Returns false
    // (with the problem shown) when the details need attention.
    async savePaymentMethod() {
      const details = this.cardDetails();
      const problem = this.cardProblem(details);
      if (problem) {
        this.setCardStatus(problem[1]);
        problem[0]?.focus();
        return false;
      }
      // In memory only: identifies the card already saved in this session.
      const fingerprint = [
        this.sessionToken, details.name, details.number.slice(-4),
        details.number.length, details.month, details.year
      ].join('|');
      if (this.savedCard === fingerprint) return true;
      if (!this.cseKey) {
        this.setCardStatus('Card details can’t be taken online right now. Please call 1300 365 487.');
        return false;
      }
      let ecrypt;
      try {
        ecrypt = await loadEcrypt();
      } catch (error) {
        this.setCardStatus('Secure card encryption did not load. Refresh the page and try again.');
        return false;
      }
      const number = ecrypt.encryptValue(details.number, this.cseKey);
      const cvn = ecrypt.encryptValue(details.cvv, this.cseKey);
      if (typeof number !== 'string' || !number || typeof cvn !== 'string' || !cvn) {
        this.setCardStatus('Secure card encryption failed. Check your card details and try again.');
        return false;
      }
      this.setCardStatus('');
      await this.post('/payment-method', {
        session_token: this.sessionToken,
        hold_token: this.hold.token,
        card: {
          name: details.name,
          number,
          expiryMonth: details.month,
          expiryYear: details.year,
          cvn
        },
        // Scheduling the booking with this card is the customer's consent.
        consent: true
      });
      this.savedCard = fingerprint;
      return true;
    }

    clearHoldState() {
      this.stopHoldTimer();
      this.hold.status = 'none';
      this.hold.token = '';
      this.hold.date = '';
      this.hold.expiresAt = 0;
      this.hold.pending = null;
      this.clearSelectedDate();
      this.renderWeek();
    }

    async releaseToken(token, reason, useBeacon = false) {
      if (!token || !this.sessionToken || this.hold.confirmed) return;
      const payload = {
        session_token: this.sessionToken,
        hold_token: token,
        reason: reason || 'released'
      };
      if (useBeacon && navigator.sendBeacon) {
        try {
          navigator.sendBeacon(
            this.endpoint('/hold/release'),
            new Blob([JSON.stringify(payload)], { type: 'application/json' })
          );
          return;
        } catch (error) {
          return;
        }
      }
      try {
        await this.post('/hold/release', payload, { keepalive: true });
      } catch (error) {
        // The lease expiry is the backstop when best-effort release cannot land.
      }
    }

    async releaseCurrentHold(reason) {
      const token = this.hold.token;
      this.hold.operation += 1;
      this.clearHoldState();
      if (token) await this.releaseToken(token, reason);
    }

    releaseOnPageHide() {
      if (this.hold.confirming || this.hold.confirmed) return;
      const token = this.hold.token;
      if (!token) return;
      this.releaseToken(token, 'pagehide', true);
    }

    detailsChanged(event) {
      const target = event.target;
      if (!target || target === this.locationInput || target === this.dateInput) return;
      if (target.closest?.('[data-public-service-turnstile]')) return;
      // Card details belong to the held date; editing them never releases it.
      if (target.closest?.('[data-public-service-card]')) {
        this.setCardStatus('');
        return;
      }
      this.maybeStartSecurity();
      if (!this.hold.token && this.hold.status !== 'acquiring') return;
      this.releaseCurrentHold('details_changed');
      this.setBookingStatus('Your details changed. Please select the booking date again.', 'error');
    }

    async submit(event) {
      event.preventDefault();
      if (this.hold.confirming) return;
      this.setFormStatus('', 'error');
      if (!this.validateForm()) return;
      if (!this.sessionToken) {
        this.setBookingStatus('Complete the security check before booking.', 'error');
        this.calendar?.focus({ preventScroll: true });
        return;
      }
      if (this.hold.pending) {
        try {
          await this.hold.pending;
        } catch (error) {
          // The hold handler has already shown the actionable error.
        }
      }
      if (!this.dateInput?.value || !this.hold.token || this.hold.status !== 'held') {
        this.setBookingStatus('Select an available booking date before booking.', 'error');
        this.calendar?.focus({ preventScroll: true });
        return;
      }
      if (this.hold.expiresAt && this.hold.expiresAt <= Date.now()) {
        await this.holdExpired();
        return;
      }

      this.hold.confirming = true;
      this.setSubmitting(true);
      try {
        let paymentChoice = '';
        if (this.paymentRequired) {
          paymentChoice = this.paymentChoice();
          if (!paymentChoice) {
            this.setCardStatus('Choose how you’d like to provide your payment method.');
            this.cardChoices[0]?.focus();
            return;
          }
          if (paymentChoice === 'now' && !(await this.savePaymentMethod())) return;
        }
        let journeyId = '';
        try {
          journeyId = (window.MCSJourney && window.MCSJourney.id) || '';
        } catch (error) { /* book without a journey id */ }
        const result = await this.post('/confirm', {
          session_token: this.sessionToken,
          hold_token: this.hold.token,
          ...(paymentChoice ? { payment_method_choice: paymentChoice } : {}),
          ...(journeyId ? { journey_id: journeyId } : {})
        });
        if (result.success !== true) throw new BookingApiError(this.submitErrorMessage);
        this.hold.confirmed = true;
        this.stopHoldTimer();
        // Google Ads "Service booking confirmed". The booking id dedupes a
        // retried confirm that the portal answers as a duplicate.
        try {
          if (result.booking_id && typeof window.gtag === 'function') {
            if (window.mcsAdsUserData) {
              const fields = this.form.elements;
              window.mcsAdsUserData(fields.namedItem('email')?.value, fields.namedItem('phone')?.value);
            }
            window.gtag('event', 'conversion', {
              send_to: 'AW-1042935060/zfvpCNzpoowdEJTap_ED',
              transaction_id: `service-booking-${result.booking_id}`
            });
          }
        } catch (error) { /* tracking must never block the booking */ }
        // Announced before the reset so the page can read what was booked. A
        // page that shows its own confirmation cancels the event.
        const shown = !this.root.dispatchEvent(new CustomEvent('public-service-booking:confirmed', {
          bubbles: true,
          cancelable: true,
          detail: { result }
        }));
        if (!shown) this.setFormStatus(this.successMessage, 'success');
        this.form.reset();
        this.savedCard = '';
        this.sessionToken = '';
        this.securityState = 'loading';
        this.securityErrorMessage = '';
        this.requestId = newRequestId();
        this.formStartedAt = new Date().toISOString();
        this.resetTurnstileRetryBudget();
        this.removeTurnstileWidget();
      } catch (error) {
        if (CARD_ERROR_CODES.has(error.code)) {
          this.savedCard = '';
          this.setCardStatus(error.message || 'Check your card details and try again.');
          this.cardSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (this.handleSessionError(error)) {
          this.setFormStatus('Your booking session expired. Complete the security check and select the date again.', 'error');
        } else if (error.code === 'hold_expired' || error.code === 'booking_conflict' || error.code === 'invalid_hold') {
          this.clearHoldState();
          await this.fetchAvailability(error.message || 'That date is no longer available. Please choose another date.');
          this.setFormStatus(error.message || this.submitErrorMessage, 'error');
        } else {
          this.setFormStatus(error.message || this.submitErrorMessage, 'error');
        }
      } finally {
        this.hold.confirming = false;
        this.setSubmitting(false);
      }
    }

    handleSessionError(error) {
      if (!error || !SESSION_ERROR_CODES.has(error.code)) return false;
      this.sessionToken = '';
      this.securityState = 'loading';
      this.securityErrorMessage = '';
      this.requestId = newRequestId();
      this.resetTurnstileRetryBudget();
      this.hold.operation += 1;
      this.clearHoldState();
      this.resetCalendar('Your booking session expired. Complete the security check again.');
      this.removeTurnstileWidget();
      this.maybeStartSecurity();
      this.startSecurityEligibilityWatcher();
      return true;
    }

    afterReset() {
      this.hold.operation += 1;
      this.stopHoldTimer();
      this.hold.status = 'none';
      this.hold.token = '';
      this.hold.date = '';
      this.hold.expiresAt = 0;
      this.hold.pending = null;
      this.sessionToken = '';
      this.securityState = 'waiting';
      this.securityErrorMessage = '';
      this.requestId = newRequestId();
      this.resetTurnstileRetryBudget();
      this.removeTurnstileWidget();
      this.resetCalendar();
      this.startSecurityEligibilityWatcher();
      if (this.hold.confirmed) {
        window.setTimeout(() => {
          this.hold.confirmed = false;
        }, 0);
      }
    }
  }

  const initialise = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-public-service-booking]')) roots.push(scope);
    scope.querySelectorAll?.('[data-public-service-booking]').forEach((root) => roots.push(root));
    roots.forEach((root) => new PublicServiceBooking(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
